import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { storage } from '../firebase';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { SUBJECTS, CHAPTERS_BY_SUBJECT } from '../data/notes';
import { SCHOOL_PROGRAMS } from '../data/schoolNotes/index';
import { resolveText, getPreferredLanguage } from '../utils/bilingualResolver';
import { 
    FaBook, FaHistory, FaFilePdf, FaDownload, 
    FaEye, FaGraduationCap, FaBookOpen, FaClipboardList, FaSearch
} from 'react-icons/fa';
import styles from './StudyMaterial.module.css';
import studyMobileImg from '../pages/image/prrepx_study0.png';
import pdfpageJpg from '../pages/image/study_material.png';

/* ─── Cheatsheet data ─────────────────────────────────────── */
// Primary filters: exam / class
const CS_PRIMARY_FILTERS = [
    { id: 'jeeneet', label: 'JEE / NEET' },
    { id: 'class12', label: 'Class 12'  },
    { id: 'class11', label: 'Class 11'  },
    { id: 'class10', label: 'Class 10'  },
    { id: 'class9',  label: 'Class 9'   },
    { id: 'class8',  label: 'Class 8'   },
    { id: 'class7',  label: 'Class 7'   },
    { id: 'class6',  label: 'Class 6'   },
];

// Subject sub-filters per primary category
const CS_SUBJECT_FILTERS_MAP = {
    jeeneet:  [
        { id: 'all',         label: 'All'         },
        { id: 'chemistry',   label: 'Chemistry'   },
        { id: 'physics',     label: 'Physics'     },
        { id: 'biology',     label: 'Biology'     },
        { id: 'mathematics', label: 'Mathematics' },
    ],
    class12: [
        { id: 'all',         label: 'All'         },
        { id: 'chemistry',   label: 'Chemistry'   },
        { id: 'physics',     label: 'Physics'     },
        { id: 'biology',     label: 'Biology'     },
        { id: 'mathematics', label: 'Mathematics' },
    ],
    class11: [
        { id: 'all',         label: 'All'         },
        { id: 'chemistry',   label: 'Chemistry'   },
        { id: 'physics',     label: 'Physics'     },
        { id: 'biology',     label: 'Biology'     },
        { id: 'mathematics', label: 'Mathematics' },
    ],
    class10: [
        { id: 'all',         label: 'All'         },
        { id: 'mathematics', label: 'Mathematics' },
        { id: 'science',     label: 'Science'     },
        { id: 'social',      label: 'Social Studies' },
        { id: 'english',     label: 'English'     },
    ],
    class9: [
        { id: 'all',         label: 'All'         },
        { id: 'mathematics', label: 'Mathematics' },
        { id: 'science',     label: 'Science'     },
        { id: 'social',      label: 'Social Studies' },
        { id: 'english',     label: 'English'     },
    ],
    class8: [
        { id: 'all',         label: 'All'         },
        { id: 'mathematics', label: 'Mathematics' },
        { id: 'science',     label: 'Science'     },
        { id: 'social',      label: 'Social Studies' },
        { id: 'english',     label: 'English'     },
        { id: 'hindi',       label: 'Hindi'       },
    ],
    class7: [
        { id: 'all',         label: 'All'         },
        { id: 'mathematics', label: 'Mathematics' },
        { id: 'science',     label: 'Science'     },
        { id: 'social',      label: 'Social Studies' },
        { id: 'english',     label: 'English'     },
        { id: 'hindi',       label: 'Hindi'       },
    ],
    class6: [
        { id: 'all',         label: 'All'         },
        { id: 'mathematics', label: 'Mathematics' },
        { id: 'science',     label: 'Science'     },
        { id: 'social',      label: 'Social Studies' },
        { id: 'english',     label: 'English'     },
        { id: 'hindi',       label: 'Hindi'       },
    ],
};

// exam: ['jee'] | ['neet'] | ['jee','neet']
// cls:  'class11' | 'class12' | 'both'
const SUBJECT_THUMBS = {
    chemistry:   '/cheatsheets/thumbs/chemistry.svg',
    physics:     '/cheatsheets/thumbs/physics.svg',
    biology:     '/cheatsheets/thumbs/biology.svg',
    mathematics: '/cheatsheets/thumbs/math.svg',
    science:     '/cheatsheets/thumbs/physics.svg',
    social:      '/cheatsheets/thumbs/biology.svg',
    english:     '/cheatsheets/thumbs/math.svg',
    hindi:       '/cheatsheets/thumbs/chemistry.svg',
};

// Map non-standard subjects to existing card-color classes
const SUBJECT_CARD_CLASS = {
    chemistry:   'csCard_chemistry',
    physics:     'csCard_physics',
    biology:     'csCard_biology',
    mathematics: 'csCard_mathematics',
    science:     'csCard_science',
    social:      'csCard_social',
    english:     'csCard_english',
    hindi:       'csCard_hindi',
};

const SECTION_DESCRIPTIONS = {
    chapterwise: 'Build strong concepts chapter-by-chapter with crisp notes having all formulas, concepts & results in one places.',
    handbooks: 'Get complete syllabus mastery with all-in-one handbooks crafted for maximum marks in minimum revision time.',
    cheatsheets: 'Revise smarter with quick, high-scoring cheatsheets built for last-minute recall before tests.',
    pyq: 'Practice real exam-level PYQs year-wise to improve speed, accuracy, and confidence under actual pattern.',
    ncert: 'Learn directly from NCERT-backed material with clean chapter flow, concept clarity, and board-ready preparation.',
};

const getBilingualTitle = (value) => {
    if (value && typeof value === 'object') {
        const english = value.english || value.hindi || '';
        const hindi = value.hindi || value.english || '';
        return { english, hindi };
    }
    return { english: value || '', hindi: '' };
};

const CS_SHEETS = [
    // ── Chemistry ──
    { subject:'chemistry', exam:['jee','neet'], file:'Chem_01_Exception_Encyclopedia.html', num:'Chem 01', title:'Exception Encyclopedia',         desc:'IE anomalies (Be>B, N>O), Lanthanide Contraction, Diagonal Relationships, S-block burning products, Inert Pair Effect.', tags:['P-Block','S-Block','Periodic Trends'], hot:true },
    { subject:'chemistry', exam:['jee','neet'],     file:'Chem_02_Color_Flame_Map.html',        num:'Chem 02', title:'Color & Flame Map',              desc:'Silver halide precipitates (AgF soluble!), flame test colors for all metals, transition metal solution colors, KMnO₄ n-factor in 3 media.', tags:['Inorganic','Qualitative','Flame Tests'], hot:true },
    { subject:'chemistry', exam:['jee','neet'],  file:'Chem_03_Hybridization_Geometry.html', num:'Chem 03', title:'Hybridization & Geometry',       desc:'All 12 VSEPR geometries, Xenon fluorides (XeF₂/XeF₄/XeF₆), interhalogen compounds, bond angle trends, lone pair effects.', tags:['VSEPR','Xenon','Geometry'], hot:false },
    { subject:'chemistry', exam:['jee','neet'],    file:'Chem_04_PhysChem_Formula_Finisher.html', num:'Chem 04', title:'Physical Chem Formula Finisher', desc:'Thermodynamics (ΔU, ΔH, Gibbs), Chemical Kinetics (Arrhenius), Electrochemistry (Nernst 0.0592/n), Colligative properties, Van\'t Hoff factor.', tags:['Kinetics','Thermo','Electrochem'], hot:true },
    { subject:'chemistry', exam:['jee','neet'],  file:'Misc_01_Unit_Constant_Killer.html',        num:'Chem 05', title:'Unit & Constant Killer',          desc:'R in SI vs atm·L, Planck\'s h, Boltzmann kB, Avogadro NA, G vs g. Temperature conversions, pressure units, energy unit traps, Nernst 0.0592 only at 298K.', tags:['Constants','Units','Conversions'], hot:true },
    { subject:'chemistry', exam:['jee','neet'], file:'Misc_02_Graph_Master.html',                num:'Chem 06', title:'Graph Master',                    desc:'Zero/first-order kinetics graphs, Arrhenius ln k vs 1/T (slope=−Ea/R), stopping potential vs frequency, P-V adiabatic vs isothermal, Maxwell distribution.', tags:['Graphs','Kinetics','Gases'], hot:false },
    { subject:'chemistry', exam:['jee','neet'], file:'Misc_09_Environmental_Chemistry.html',     num:'Chem 07', title:'Environmental Chemistry',         desc:'PAN=secondary pollutant, photochemical vs London smog, high BOD=polluted, Minamata=Mercury/Itai-Itai=Cadmium, bactericidal vs bacteriostatic, soaps fail in hard water.', tags:['Environment','Drugs','BOD'], hot:false },
     { subject:'chemistry', exam:['jee','neet'], file:'Misc_06_Organic_Roadmap.html',             num:'Chem 08', title:'Organic Roadmap',                 desc:'Complete functional group interconversion: Alkane→Halide→Alkene→Alcohol→Aldehyde→Acid, Hofmann bromamide (n-1 carbons!), aq. KOH vs alc. KOH, PCC vs KMnO₄.', tags:['Organic','Reagents','Interconversions'], hot:true },
    // ── Physics ──
    { subject:'physics', exam:['jee','neet'],  file:'Phys_01_Sign_Convention_Master.html',   num:'Phys 01', title:'Sign Convention Master',         desc:'Ray Optics: New Cartesian sign convention, mirrors vs lenses, YDSE, single-slit diffraction, Malus\'s law, Brewster\'s angle.', tags:['Optics','YDSE','Wave Optics'], hot:true },
    { subject:'physics', exam:['jee','neet'],  file:'Phys_02_Modern_Physics_Constants.html', num:'Phys 02', title:'Modern Physics Constants',        desc:'E=hc/λ=1240 eV·nm, Photoelectric (stopping potential), de Broglie, Bohr model (−13.6Z²/n²), radioactive decay, mass defect 1u=931.5 MeV.', tags:['Modern Physics','Bohr Model','Radioactivity'], hot:true },
    { subject:'physics', exam:['jee','neet'],  file:'Phys_03_Circuit_Shortcut_Sheet.html',   num:'Phys 03', title:'Circuit Shortcut Sheet',          desc:'KVL/KCL, Wheatstone bridge (PS=QR), Capacitor (t=0 short, t=∞ open), LC resonance, Star-Delta, cube resistance, Thevenin\'s theorem.', tags:['Circuits','Capacitor','KVL/KCL'], hot:false },
    { subject:'physics', exam:['jee','neet'],  file:'Phys_04_Mechanics_Case_Study.html',     num:'Phys 04', title:'Mechanics Case Study',            desc:'Friction (two-body protocol), vertical circle v_min, banking angle, projectile complementary angles, Moments of Inertia, rolling without slipping.', tags:['Mechanics','Rotation','Friction'], hot:true },
    { subject:'physics',   exam:['jee','neet'],  file:'Misc_05_FBD_Shortcut_Sheet.html',          num:'Phys 05', title:'FBD Shortcut Sheet',              desc:'Atwood machine (a, T formulas), two-block string tension, inclined plane (N=mg cosθ, angle of repose tanθ=μ), lift/elevator, banked road tanθ=v²/rg.', tags:['FBD','Mechanics','Non-Inertial'], hot:true },
    { subject:'physics',   exam:['jee'],         file:'Misc_07_Logic_Gates_Semiconductor.html',   num:'Phys 06', title:'Logic Gates & Semiconductors',    desc:'All 6 gate truth tables (AND/OR/NOT/NAND/NOR/XOR), NAND & NOR as universal gates, Boolean algebra, Zener diode, p-n junction, transistor β=α/(1−α).', tags:['Logic Gates','Transistor','Zener'], hot:true },
    
    // ── Biology (NEET) ──
    { subject:'biology', exam:['neet'],  file:'Bio_01_Example_Encyclopedia.html',      num:'Bio 01', title:'Example Encyclopedia',            desc:'All NCERT classification examples: Algae types, Bryophytes, Gymnosperms (Cycas coraloid roots!), Animal kingdom, all Fungi divisions (SPAN/BAPU).', tags:['Classification','Sci. Names','Animal Kingdom'], hot:true },
    { subject:'biology', exam:['neet'],  file:'Bio_02_Bio_Cycle_Accounting.html',      num:'Bio 02', title:'Bio-Cycle Accounting',             desc:'ATP counting: Glycolysis (2 net ATP), Krebs (3 NADH+1 FADH₂+1 GTP), total 36–38 ATP, Calvin cycle (3-2 rule), C4 Kranz anatomy, nitrogen fixation.', tags:['Respiration','Photosynthesis','ATP Count'], hot:true },
    { subject:'biology', exam:['neet'],  file:'Bio_03_Genetic_Crosses_Ratios.html',    num:'Bio 03', title:'Genetic Crosses & Ratios',         desc:'All Mendelian ratios, ALL 5 epistasis types (9:3:4 / 12:3:1 / 9:7 / 15:1 / 13:3), Codominance, Lethal alleles (2:1), Sex determination systems.', tags:['Genetics','Epistasis','Ratios'], hot:true },
    { subject:'biology', exam:['neet'],  file:'Bio_04_Hormone_Gland_Matrix.html',      num:'Bio 04', title:'Hormone & Gland Matrix',           desc:'Complete endocrine: Pituitary hormones, Thyroid diseases, Adrenal cortex GFR zones (Salt/Sugar/Sex), Insulin, all 5 Plant Growth Regulators.', tags:['Endocrine','PGR','Diseases'], hot:false },
    { subject:'biology',   exam:['jee','neet'],  file:'Misc_03_NCERT_Exceptions.html',            num:'Bio 05', title:'NCERT Exceptions',                desc:'AgF soluble (unlike AgX), IE₁ anomalies, NH₃ > NF₃ bond angle, Mycoplasma (no cell wall), Mitochondria 70S ribosomes, magnetic force never does work.', tags:['Exceptions','Biology','Physics'], hot:true },
    { subject:'biology',   exam:['neet'],    file:'Misc_04_Diagram_Label_Defense.html',       num:'Bio 06', title:'Diagram Label Defense',           desc:'Heart chambers (Tricuspid=Right; Mitral=Left), SA node in right atrium, nephron flow (ADH on collecting duct), DNA antiparallel backbone, G-C=3 H-bonds.', tags:['Diagrams','Heart','Nephron'], hot:false },
    { subject:'biology',   exam:['neet'],     file:'Misc_08_Biomolecules_Polymers.html',       num:'Bio 07', title:'Biomolecules & Polymers',         desc:'Carbohydrates (α=digestible; β=cellulose=indigestible), sucrose=non-reducing, nucleoside vs nucleotide, Nylon-6 vs Nylon-6,6, Buna-S vs Buna-N, Bakelite=thermosetting.', tags:['Biomolecules','Polymers','Nylon'], hot:false },
    // ── Mathematics (JEE) ──
    { subject:'mathematics', exam:['jee'],  file:'Math_01_Conic_Comparative.html',    num:'Math 01', title:'Conic Sections Comparative',      desc:'Parabola (y²=4ax), Ellipse (c²=a²−b²), Hyperbola (c²=a²+b²), asymptotes, tangency conditions, chord of contact T=0, SS₁=T² pair of tangents.', tags:['Conics','Parabola','Ellipse'], hot:true },
    { subject:'mathematics', exam:['jee'],  file:'Math_02_Integration_Forms.html',    num:'Math 02', title:'Integration Forms',               desc:'Standard integrals, IBP with ILATE rule, e^x[f+f\'] shortcut, Partial fractions, Weierstrass substitution, Wallis formula, King property (π/4 trick).', tags:['Integration','Definite','King Rule'], hot:true },
    { subject:'mathematics', exam:['jee'],    file:'Math_03_Vector_3D_Geometry.html',   num:'Math 03', title:'Vectors & 3D Geometry',           desc:'Dot product, cross product, scalar triple product (coplanar=STP=0), direction cosines (l²+m²+n²=1), shortest distance skew lines, foot of perpendicular.', tags:['3D Geometry','Vectors','Planes'], hot:false },
    { subject:'mathematics', exam:['jee'],  file:'Math_04_Trigonometric_Transform.html', num:'Math 04', title:'Trigonometric Transforms',     desc:'Compound angles, double/triple angle formulas, Sum-to-Product (all 4 forms), conditional identities, inverse trig properties, arctan addition formula.', tags:['Trig','Inverse Trig','Identities'], hot:true },
];

/* ─── Master Handbook data ───────────────────────────────── */
// Full Master Handbooks: JEE/NEET subject-wise + Class 6–12 CBSE board handbooks.
const MASTER_HANDBOOKS = [
    // ───────── JEE / NEET Subject-wise Master Handbooks ─────────
    { subject:'mathematics', cls:'jeeneet', exam:['jee'],         file:'Math_05_JEE_Math_Master_Handbook.html',       thumb:'/cheatsheets/thumbs/math.svg',      num:'Math HB',  title:'JEE Mathematics Master Handbook',     desc:'All 23 chapters with formulas, theorems, shortcuts and JEE-ready strategy.',       tags:['Full Syllabus','JEE Main','JEE Advanced'], hot:true, chapters:23 },
    { subject:'physics',     cls:'jeeneet', exam:['jee','neet'],  file:'Phys_HB_JEE_NEET_Physics_Master_Handbook.html', thumb:'/cheatsheets/thumbs/physics.svg', num:'Phys HB',  title:'JEE/NEET Physics Master Handbook',    desc:'Mechanics to Modern Physics — formulas, graph logic and standard results.',         tags:['Full Syllabus','JEE Main','NEET'],         hot:true, chapters:19 },
    { subject:'chemistry',   cls:'jeeneet', exam:['jee','neet'],  file:'Chem_HB_JEE_NEET_Chemistry_Master_Handbook.html', thumb:'/cheatsheets/thumbs/chemistry.svg', num:'Chem HB',  title:'JEE/NEET Chemistry Master Handbook',  desc:'Physical, Organic and Inorganic — reactions, exceptions and trend logic.',          tags:['Full Syllabus','JEE Main','NEET'],         hot:true, chapters:20 },
    { subject:'biology',     cls:'jeeneet', exam:['neet'],        file:'Bio_HB_NEET_Biology_Master_Handbook.html',     thumb:'/cheatsheets/thumbs/biology.svg',  num:'Bio HB',   title:'NEET Biology Master Handbook',        desc:'38 NCERT chapters — Botany, Zoology, genetics and ecology recall points.',         tags:['Full Syllabus','NEET','NCERT'],            hot:true, chapters:38, chapterLabel:'NCERT Chapter Touchpoints' },

    // ───────── Class 12 (CBSE) ─────────
    { subject:'physics',     cls:'class12', exam:[], file:'Class12_Physics_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/physics.svg',   num:'Class 12 · Phys', title:'Class 12 Physics — Master Handbook',     desc:'Full CBSE Class 12 Physics — electrostatics, current, magnetism, EMI, AC, EM waves, optics, dual nature, atoms & nuclei, semiconductors.',                       tags:['Full Syllabus','CBSE','Handbook','JEE/NEET'], hot:true },
    { subject:'chemistry',   cls:'class12', exam:[], file:'Class12_Chemistry_Board_Handbook.html', thumb:'/cheatsheets/thumbs/chemistry.svg', num:'Class 12 · Chem', title:'Class 12 Chemistry — Master Handbook',   desc:'Full CBSE Class 12 Chemistry — solutions, electrochem, kinetics, d/f-block, coordination, haloalkanes, alcohols, aldehydes/ketones, amines, biomolecules.',     tags:['Full Syllabus','CBSE','Handbook','JEE/NEET'], hot:true },
    { subject:'mathematics', cls:'class12', exam:[], file:'Class12_Math_Board_Handbook.html',      thumb:'/cheatsheets/thumbs/math.svg',      num:'Class 12 · Math', title:'Class 12 Mathematics — Master Handbook', desc:'Full CBSE Class 12 Math — relations, inverse trig, matrices, determinants, derivatives, AOD, integrals, area, DEs, vectors, 3-D, LPP, probability.',           tags:['Full Syllabus','CBSE','Handbook','JEE'],      hot:true },
    { subject:'biology',     cls:'class12', exam:[], file:'Class12_Biology_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/biology.svg',   num:'Class 12 · Bio',  title:'Class 12 Biology — Master Handbook',     desc:'Full CBSE Class 12 Bio — reproduction, genetics, evolution, health & disease, microbes, biotech, ecology, environment.',                                       tags:['Full Syllabus','CBSE','Handbook','NEET'],     hot:true },

    // ───────── Class 11 (CBSE) ─────────
    { subject:'physics',     cls:'class11', exam:[], file:'Class11_Physics_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/physics.svg',   num:'Class 11 · Phys', title:'Class 11 Physics — Master Handbook',     desc:'Full CBSE Class 11 Physics — units, vectors, kinematics, NLM, WEP, rotation, gravitation, fluids, thermal, thermodynamics, KTG, SHM, waves.',                tags:['Full Syllabus','CBSE','Handbook','JEE/NEET'], hot:true },
    { subject:'chemistry',   cls:'class11', exam:[], file:'Class11_Chemistry_Board_Handbook.html', thumb:'/cheatsheets/thumbs/chemistry.svg', num:'Class 11 · Chem', title:'Class 11 Chemistry — Master Handbook',   desc:'Full CBSE Class 11 Chemistry — mole, atomic structure, periodicity, bonding, states of matter, thermo, equilibrium, redox, hydrogen, s-block, p-block 13-14, organic, hydrocarbons.', tags:['Full Syllabus','CBSE','Handbook','JEE/NEET'], hot:true },
    { subject:'mathematics', cls:'class11', exam:[], file:'Class11_Math_Board_Handbook.html',      thumb:'/cheatsheets/thumbs/math.svg',      num:'Class 11 · Math', title:'Class 11 Mathematics — Master Handbook', desc:'Full CBSE Class 11 Math — sets, relations, trig, complex, quadratics, P&C, binomial, sequences, lines, conics, 3-D, limits, derivatives, statistics, probability.',                  tags:['Full Syllabus','CBSE','Handbook','JEE'],      hot:true },
    { subject:'biology',     cls:'class11', exam:[], file:'Class11_Biology_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/biology.svg',   num:'Class 11 · Bio',  title:'Class 11 Biology — Master Handbook',     desc:'Full CBSE Class 11 Bio — diversity, kingdoms, plant/animal anatomy, cell, biomolecules, photosynthesis, respiration, digestion, breathing, circulation, excretion, neural, endocrine.', tags:['Full Syllabus','CBSE','Handbook','NEET'],     hot:true },

    // ───────── Class 10 (CBSE) ─────────
    { subject:'mathematics', cls:'class10', exam:[], file:'Class10_Math_Board_Handbook.html',    thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 10 · Math', title:'Class 10 Mathematics — Board Handbook',  desc:'Every chapter of CBSE Class 10 Math — full theory, formulas, theorems, traps, shortcuts, key results.',           tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'science',     cls:'class10', exam:[], file:'Class10_Science_Board_Handbook.html', thumb:'/cheatsheets/thumbs/physics.svg', num:'Class 10 · Sci',  title:'Class 10 Science — Board Handbook',       desc:'CBSE Class 10 Science (Phys + Chem + Bio) — every chapter with theory, equations, diagrams cues, definitions.', tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'social',      cls:'class10', exam:[], file:'Class10_Social_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/biology.svg', num:'Class 10 · SST',  title:'Class 10 Social Science — Board Handbook', desc:'CBSE Class 10 SST: History, Geography, Civics, Economics — full theory, dates, facts, terms, formulas.',         tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'english',     cls:'class10', exam:[], file:'Class10_English_Board_Handbook.html', thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 10 · Eng',  title:'Class 10 English — Board Handbook',       desc:'CBSE Class 10 English: grammar rules, writing formats, full chapter summaries (First Flight + Footprints).',     tags:['Full Syllabus','CBSE','Handbook'], hot:true },

    // ───────── Class 9 (CBSE) ─────────
    { subject:'mathematics', cls:'class9',  exam:[], file:'Class9_Math_Board_Handbook.html',     thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 9 · Math',  title:'Class 9 Mathematics — Board Handbook',    desc:'Full CBSE Class 9 Math — number systems, polynomials, geometry, mensuration, statistics with full theory & key results.', tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'science',     cls:'class9',  exam:[], file:'Class9_Science_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/physics.svg', num:'Class 9 · Sci',   title:'Class 9 Science — Board Handbook',        desc:'CBSE Class 9 Science (Phys+Chem+Bio) — matter, atoms, cells, tissues, motion, force, gravitation, sound, food.',           tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'social',      cls:'class9',  exam:[], file:'Class9_Social_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/biology.svg', num:'Class 9 · SST',   title:'Class 9 Social Science — Board Handbook', desc:'CBSE Class 9 SST — French/Russian Revolutions, Nazism, India physical, climate, Democracy, Constitution, Poverty.',         tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'english',     cls:'class9',  exam:[], file:'Class9_English_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 9 · Eng',   title:'Class 9 English — Board Handbook',        desc:'CBSE Class 9 English — full grammar, writing, Beehive prose & poetry, Moments supplementary chapter summaries.',           tags:['Full Syllabus','CBSE','Handbook'], hot:true },

    // ───────── Class 8 (CBSE) ─────────
    { subject:'mathematics', cls:'class8',  exam:[], file:'Class8_Math_Board_Handbook.html',     thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 8 · Math',  title:'Class 8 Mathematics — Master Handbook',    desc:'Full CBSE Class 8 Math — rationals, exponents, algebra, geometry, mensuration, comparing quantities.',         tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'science',     cls:'class8',  exam:[], file:'Class8_Science_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/physics.svg', num:'Class 8 · Sci',   title:'Class 8 Science — Master Handbook',        desc:'Full CBSE Class 8 Science — crops, microbes, materials, force, friction, sound, light, cell, reproduction.',  tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'social',      cls:'class8',  exam:[], file:'Class8_Social_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/biology.svg', num:'Class 8 · SST',   title:'Class 8 Social Science — Master Handbook', desc:'Full CBSE Class 8 SST — modern India 1757-1947, resources, Constitution, Parliament, Judiciary, social justice.', tags:['Full Syllabus','CBSE','Handbook'], hot:true },
    { subject:'english',     cls:'class8',  exam:[], file:'Class8_English_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 8 · Eng',   title:'Class 8 English — Master Handbook',        desc:'Full CBSE Class 8 English — grammar, writing, Honeydew prose & poetry, It So Happened summaries.',             tags:['Full Syllabus','CBSE','Handbook'], hot:true },

    // ───────── Class 7 (CBSE) ─────────
    { subject:'mathematics', cls:'class7',  exam:[], file:'Class7_Math_Board_Handbook.html',     thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 7 · Math',  title:'Class 7 Mathematics — Master Handbook',    desc:'Full CBSE Class 7 Math — integers, fractions, equations, geometry, congruence, comparing quantities, mensuration, algebra, exponents, symmetry.', tags:['Full Syllabus','CBSE','Handbook'], hot:false },
    { subject:'science',     cls:'class7',  exam:[], file:'Class7_Science_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/physics.svg', num:'Class 7 · Sci',   title:'Class 7 Science — Master Handbook',        desc:'Full CBSE Class 7 Science — nutrition, heat, acids/bases, climate, respiration, transport, reproduction, motion, electricity, light, water, forests.', tags:['Full Syllabus','CBSE','Handbook'], hot:false },
    { subject:'social',      cls:'class7',  exam:[], file:'Class7_Social_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/biology.svg', num:'Class 7 · SST',   title:'Class 7 Social Science — Master Handbook', desc:'Full CBSE Class 7 SST — medieval India, Sultanate, Mughals, Bhakti-Sufi, environment, biomes, equality, state govt, gender, media, markets.',     tags:['Full Syllabus','CBSE','Handbook'], hot:false },
    { subject:'english',     cls:'class7',  exam:[], file:'Class7_English_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 7 · Eng',   title:'Class 7 English — Master Handbook',        desc:'Full CBSE Class 7 English — grammar, writing, Honeycomb prose & poetry, An Alien Hand summaries.',                                                tags:['Full Syllabus','CBSE','Handbook'], hot:false },

    // ───────── Class 6 (CBSE) ─────────
    { subject:'mathematics', cls:'class6',  exam:[], file:'Class6_Math_Board_Handbook.html',     thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 6 · Math',  title:'Class 6 Mathematics — Master Handbook',    desc:'Full CBSE Class 6 Math — numbers, integers, fractions, decimals, geometry, mensuration, algebra, ratio, symmetry, data handling.',                tags:['Full Syllabus','CBSE','Handbook'], hot:false },
    { subject:'science',     cls:'class6',  exam:[], file:'Class6_Science_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/physics.svg', num:'Class 6 · Sci',   title:'Class 6 Science — Master Handbook',        desc:'Full CBSE Class 6 Science — food, materials, fibres, separation, plants, body, motion, light, electricity, magnets, air, water, garbage.',          tags:['Full Syllabus','CBSE','Handbook'], hot:false },
    { subject:'social',      cls:'class6',  exam:[], file:'Class6_Social_Board_Handbook.html',   thumb:'/cheatsheets/thumbs/biology.svg', num:'Class 6 · SST',   title:'Class 6 Social Science — Master Handbook', desc:'Full CBSE Class 6 SST — Stone Age, Indus, Vedic, Mahajanapadas, Mauryas, Earth, globe, maps, government, panchayat, urban admin.',                tags:['Full Syllabus','CBSE','Handbook'], hot:false },
    { subject:'english',     cls:'class6',  exam:[], file:'Class6_English_Board_Handbook.html',  thumb:'/cheatsheets/thumbs/math.svg',    num:'Class 6 · Eng',   title:'Class 6 English — Master Handbook',        desc:'Full CBSE Class 6 English — grammar, writing, Honeysuckle prose & poetry, A Pact with the Sun summaries.',                                          tags:['Full Syllabus','CBSE','Handbook'], hot:false },
];

const StudyMaterial = () => {
    const location = useLocation();
    // { catId: subId } — which subcategory is active per category
    const [activeSub, setActiveSub] = useState({});
    // { 'catId/subId': [...files] } — cached materials
    const [materialsCache, setMaterialsCache] = useState({});
    // Set of 'catId/subId' keys currently loading
    const [loadingKeys, setLoadingKeys] = useState(new Set());
    const [downloadProgress, setDownloadProgress] = useState({});
    const [activeTab, setActiveTab] = useState('chapterwise');
    const [csFilter, setCsFilter] = useState('chemistry');
    const [csPrimary, setCsPrimary] = useState('jeeneet');
    const [hbPrimary, setHbPrimary] = useState('jeeneet');
    const [notesFilter, setNotesFilter] = useState('mathematics');
    const [notesSearch, setNotesSearch] = useState('');
    const [notesClassFilter, setNotesClassFilter] = useState('jeeneet');
    const [notesSchoolSubject, setNotesSchoolSubject] = useState('science');
    const [hbSearch, setHbSearch] = useState('');
    const [csSearch, setCsSearch] = useState('');
    const [librarySearch, setLibrarySearch] = useState({ pyq: '', ncert: '' });
    const [language, setLanguage] = useState(getPreferredLanguage);
    const csRef = useRef(null);
    const hbRef = useRef(null);
    const notesRef = useRef(null);
    // Ref to prevent duplicate fetches
    const fetchedRef = useRef(new Set());

    // Follow account-level language changes synced from Firestore (or made
    // in the viewer) without requiring a page reload.
    useEffect(() => {
        const onLangSync = (e) => { if (e.detail) setLanguage(e.detail); };
        window.addEventListener('ppx-lang', onLangSync);
        return () => window.removeEventListener('ppx-lang', onLangSync);
    }, []);

    const libraryConfig = [
        {
            id: 'pyq',
            mainCategory: "Yearwise PYQs",
            icon: <FaHistory />,
            subcategories: [
                { 
                    id: 'jee-mains', 
                    label: 'JEE Mains Yearwise Paper eBook', 
                    description: 'Comprehensive collection of JEE Mains year-wise papers (5, 10, and 20 years) merged with official answer keys.', 
                    tags: ['JEE Mains', 'PYQ eBook', 'Yearwise'] 
                },
                { 
                    id: 'neet-papers', 
                    label: 'NEET Yearwise Paper eBook', 
                    description: 'Extensive collection of NEET previous year question papers with detailed solutions and answer keys.', 
                    tags: ['NEET', 'PYQ eBook', 'Yearwise'] 
                },
                { 
                    id: 'jee-adv', 
                    label: 'JEE ADV Yearwise Paper eBook', 
                    description: 'High-quality collection of JEE Advanced previous year papers to master the toughest engineering entrance.', 
                    tags: ['JEE Advanced', 'PYQ eBook', 'Yearwise'] 
                },
            ]
        },
        {
            id: 'ncert',
            mainCategory: "NCERT Book",
            icon: <FaBook />,
            subcategories: [
                { id: 'class12', label: 'Class 12th', description: 'Download the official Class 12th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Physics','Chemistry','Mathematics','English'] },
                { id: 'class11', label: 'Class 11th', description: 'Download the official Class 11th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Physics','Chemistry','Mathematics','Biology','English'] },
                { id: 'class10', label: 'Class 10th', description: 'Download the official Class 10th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Mathematics', 'Science', 'English', 'Social Science'] },
                { id: 'class9', label: 'Class 9th', description: 'Download the official Class 9th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Mathematics', 'Science', 'English', 'Social Science'] },
                { id: 'class8', label: 'Class 8th', description: 'Download the official Class 8th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Mathematics', 'Science', 'Hindi', 'English', 'Social Science'] },
                { id: 'class7', label: 'Class 7th', description: 'Download the official Class 7th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Mathematics', 'Science', 'Hindi', 'English', 'Social Science'] },
                { id: 'class6', label: 'Class 6th', description: 'Download the official Class 6th NCERT textbooks for all subjects in high-quality PDF format.', tags: ['Mathematics', 'Science', 'Hindi', 'English', 'Social Science'] },
            ]
        },
    ];

    const fetchMaterials = useCallback(async (catId, subId) => {
        const cacheKey = `${catId}/${subId}`;
        if (fetchedRef.current.has(cacheKey)) return;
        fetchedRef.current.add(cacheKey);

        setLoadingKeys(prev => new Set([...prev, cacheKey]));
        try {
            const folderRef = ref(storage, `materials/${catId}/${subId}`);
            const res = await listAll(folderRef);
            const allFiles = await Promise.all(res.items.map(async (item) => {
                const url = await getDownloadURL(item);
                const isImage = /\.(jpg|jpeg|png|webp)$/i.test(item.name);
                const isPdf = /\.pdf$/i.test(item.name);
                const baseName = item.name.split('.').slice(0, -1).join('.');
                return { id: item.fullPath, name: item.name, baseName, url, isImage, isPdf };
            }));
            const pdfFiles = allFiles.filter(f => f.isPdf);
            const imageFiles = allFiles.filter(f => f.isImage);
            const paired = pdfFiles.map(pdf => {
                const thumb = imageFiles.find(img => img.baseName === pdf.baseName);
                return {
                    id: pdf.id,
                    title: pdf.baseName.replaceAll('_', ' '),
                    pdfUrl: pdf.url,
                    thumbUrl: thumb ? thumb.url : null
                };
            });
            setMaterialsCache(prev => ({ ...prev, [cacheKey]: paired }));
        } catch (err) {
            console.error(err);
            setMaterialsCache(prev => ({ ...prev, [cacheKey]: [] }));
        } finally {
            setLoadingKeys(prev => { const s = new Set(prev); s.delete(cacheKey); return s; });
        }
    }, []);

    // Set initial activeSub (first subcategory for each category) and fetch data
    useEffect(() => {
        const initial = {};
        libraryConfig.forEach(cat => {
            if (cat.subcategories.length > 0) {
                initial[cat.id] = cat.subcategories[0].id;
                fetchMaterials(cat.id, cat.subcategories[0].id);
            }
        });
        setActiveSub(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const section = location.state?.returnSection;
        const returnTab = location.state?.returnTab;
        const hash = location.hash;

        let targetRef = null;
        if (section === 'chapterwise' || hash === '#chapterwise-notes') {
            targetRef = notesRef;
        } else if (section === 'handbooks' || hash === '#master-handbooks') {
            targetRef = hbRef;
        } else if (section === 'cheatsheets' || hash === '#cheatsheets' || hash === '#revision-sheets') {
            targetRef = csRef;
        }

        if (!targetRef) return;

        const resolvedTab =
            returnTab ||
            (section === 'chapterwise'
                ? 'chapterwise'
                : section === 'handbooks'
                    ? 'handbooks'
                    : section === 'cheatsheets'
                        ? 'sheets'
                        : hash === '#chapterwise-notes'
                            ? 'chapterwise'
                            : hash === '#master-handbooks'
                                ? 'handbooks'
                                : hash === '#cheatsheets' || hash === '#revision-sheets'
                                    ? 'sheets'
                                        : 'chapterwise');

        // Restore school class/subject filter when returning from a school chapter
        const returnProgramKey = location.state?.returnProgramKey;
        if (returnProgramKey) {
            const parts = returnProgramKey.split('-');
            if (parts.length >= 2) {
                setNotesClassFilter(parts[0]);      // e.g. 'class10'
                setNotesSchoolSubject(parts[1]);    // e.g. 'science' or 'mathematics'
            }
        }

        let attempt = 0;
        let rafId = 0;
        const maxAttempts = 12;

        const scrollToTarget = () => {
            const el = targetRef.current;
            if (!el) {
                if (attempt < maxAttempts) {
                    attempt += 1;
                    rafId = window.requestAnimationFrame(scrollToTarget);
                }
                return;
            }

            const rootStyles = getComputedStyle(document.documentElement);
            const navHeightVar = parseInt(rootStyles.getPropertyValue('--nav-height-total'), 10);
            const navOffset = Number.isFinite(navHeightVar) ? navHeightVar : 106;
            const extraGap = 14;
            const y = el.getBoundingClientRect().top + window.scrollY - navOffset - extraGap;

            window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        };

        const timer = window.setTimeout(() => {
            scrollToTarget();
        }, 80);

        return () => {
            window.clearTimeout(timer);
            if (rafId) window.cancelAnimationFrame(rafId);
        };
    }, [location.key, location.hash, location.state]);

    const revisionSubjectFilters = CS_SUBJECT_FILTERS_MAP.jeeneet;
    const notesSubjects = SUBJECTS.filter((s) => (CHAPTERS_BY_SUBJECT[s.id] || []).length > 0);
    const notesFilters = notesSubjects.map((s) => ({ id: s.id, label: s.label }));

    const isSchoolMode = notesClassFilter !== 'jeeneet';
    const schoolProgram = useMemo(
        () => isSchoolMode ? SCHOOL_PROGRAMS.find((p) => p.key === `${notesClassFilter}-${notesSchoolSubject}`) || null : null,
        [isSchoolMode, notesClassFilter, notesSchoolSubject]
    );

    const chapterCards = isSchoolMode
        ? (schoolProgram?.chapters || []).map((ch, idx) => ({
            ...ch,
            _school: true,
            programKey: schoolProgram.key,
            chapterNo: idx + 1,
          }))
        : (CHAPTERS_BY_SUBJECT[notesFilter] || []).map((ch, idx) => ({
            ...ch,
            subjectMeta: notesSubjects.find((s) => s.id === notesFilter),
            chapterNo: idx + 1,
          }));

    const filteredChapterCards = useMemo(() => {
        const q = notesSearch.trim().toLowerCase();
        if (!q) return chapterCards;

        return chapterCards.filter((ch) => {
            const title = getBilingualTitle(ch.title);
            const haystack = [
                title.english,
                title.hindi,
                ch.slug,
                `chapter ${ch.chapterNo}`,
                ch.subjectMeta?.label || '',
                ch.exam || '',
            ]
                .join(' ')
                .toLowerCase();

            return haystack.includes(q);
        });
    }, [chapterCards, notesSearch]);

    const filteredHandbooks = useMemo(() => {
        const q = hbSearch.trim().toLowerCase();
        return MASTER_HANDBOOKS.filter((hb) => {
            const primaryMatch = hb.cls === hbPrimary;
            const searchMatch =
                !q ||
                [hb.title, hb.desc, hb.num, hb.subject, ...(hb.tags || [])]
                    .join(' ')
                    .toLowerCase()
                    .includes(q);
            return primaryMatch && searchMatch;
        });
    }, [hbPrimary, hbSearch]);

    const filteredSheets = useMemo(() => {
        const q = csSearch.trim().toLowerCase();
        return CS_SHEETS.filter((s) => {
            const isJeeNeet = s.exam && (s.exam.includes('jee') || s.exam.includes('neet'));
            const subjectMatch = csFilter === 'all' || s.subject === csFilter;
            const searchMatch =
                !q ||
                [s.title, s.desc, s.num, s.subject, ...(s.tags || [])]
                    .join(' ')
                    .toLowerCase()
                    .includes(q);
            return isJeeNeet && subjectMatch && searchMatch;
        });
    }, [csFilter, csSearch]);

    const handleDownload = (url, fileName, fileId) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = Math.round((event.loaded / event.total) * 100);
                setDownloadProgress(prev => ({ ...prev, [fileId]: percentComplete }));
            }
        };
        xhr.onload = () => {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${fileName.trim()}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }
            setTimeout(() => {
                setDownloadProgress(prev => {
                    const newState = { ...prev };
                    delete newState[fileId];
                    return newState;
                });
            }, 2000);
        };
        xhr.onerror = () => {
            window.open(url, '_blank');
        };
        xhr.send();
    };

    return (
        <div className={styles.wrapper}>
            {/* ── Hero (matches Home page layout) ── */}
            <div className={styles.hero} style={{ backgroundImage: `url(${pdfpageJpg})` }}>
                <div className={`${styles.heroGlow} ${styles.glow1}`} />
                <div className={`${styles.heroGlow} ${styles.glow2}`} />
                <div className={`${styles.heroGlow} ${styles.glow3}`} />

                {/* Left image — desktop only, hidden via CSS */}
                <div className={styles.heroImgLeft}>
                    <div className={styles.heroImgFrameLeft}>
                        <img src={studyMobileImg} alt="Study Materials" className={styles.heroMainImg} />
                        <div className={styles.heroImgOverlay} />
                    </div>
                </div>

                {/* Mobile image — shown on mobile, hidden on desktop/tablet */}
                <div className={styles.heroMobileImg}>
                    <img src={studyMobileImg} alt="Study Materials" className={styles.heroMobileSlideImg} />
                    <div className={styles.heroMobileImgBadge}>PrrepX Digital Library — 100% Free</div>
                </div>

                {/* Center — text content */}
                <div className={styles.heroContent}>
                    <div className={styles.heroBadgePill}>🎓 PrrepX Digital Library — Unlimited</div>
                    <h1 className={styles.heroHeading}>
                        <span className={styles.heroBlue}>Premium Study Materials</span>
                        <br />
                        <span className={styles.heroAccent}>Available for everyone</span>
                    </h1>
                    <p className={styles.heroTagline}>
                        {/* <strong>Chapterwise Notes · Master Handbook · High Scoring Cheatsheets · PYQ eBooks · NCERT Books</strong> — compiled in one place. Upcoming more. */}
                        All high-quality study materials you need for class 6-12th & JEE/NEET exam success—all in one place.
                    </p>

                    <p className={styles.alumniText}>✨ Led by 100% IIT/NIT &amp; top Medical Alumni</p>
                </div>

                {/* Right image — desktop only, hidden via CSS */}
                <div className={styles.heroImageCol}>
                    <div className={styles.heroImgFrame}>
                        <img src={studyMobileImg} alt="Study Materials" className={styles.heroMainImg} />
                        <div className={styles.heroImgOverlay} />
                    </div>
                </div>

                <div className={styles.heroScrollHint}>
                    <span>Scroll to explore</span>
                    <div className={styles.scrollArrow} />
                </div>
            </div>

            {/* ── TopBar ── */}
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <div className={styles.topBarLeft}>
                        <span className={styles.topBarIcon}><FaBook /></span>
                        <span className={styles.topBarTitle}>Study Materials</span>
                    </div>
                    <div className={styles.topBarTabs}>
                        <button
                            className={`${styles.topBarTabBtn} ${activeTab === 'chapterwise' ? styles.topBarTabBtnActive : ''}`}
                            onClick={() => {
                                setActiveTab('chapterwise');
                                setTimeout(() => notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                            }}
                        >
                            <FaGraduationCap /> Notes
                        </button>
                        <button
                            className={`${styles.topBarTabBtn} ${activeTab === 'handbooks' ? styles.topBarTabBtnActive : ''}`}
                            onClick={() => {
                                setActiveTab('handbooks');
                                setTimeout(() => hbRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                            }}
                        >
                            <FaBookOpen /> Handbook
                        </button>
                        <button
                            className={`${styles.topBarTabBtn} ${activeTab === 'sheets' ? styles.topBarTabBtnActive : ''}`}
                            onClick={() => {
                                setActiveTab('sheets');
                                setTimeout(() => csRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                            }}
                        >
                            <FaClipboardList /> Cheatsheets
                        </button>
                        <button
                            className={`${styles.topBarTabBtn} ${activeTab === 'pyq' ? styles.topBarTabBtnActive : ''}`}
                            onClick={() => setActiveTab('pyq')}
                        >
                            <FaHistory /> PYQ eBook
                        </button>
                        <button
                            className={`${styles.topBarTabBtn} ${activeTab === 'ncert' ? styles.topBarTabBtnActive : ''}`}
                            onClick={() => setActiveTab('ncert')}
                        >
                            <FaBook /> NCERT Books
                        </button>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════
                CHAPTERWISE NOTES
                ══════════════════════════════════════════════════ */}
            {(activeTab === 'all' || activeTab === 'chapterwise') && (
            <div className={styles.container}>
                <section className={styles.categorySection} ref={notesRef} id="chapterwise-notes">
                    <div className={styles.categoryHeadingRow}>
                        <h2 className={styles.categoryHeading}>
                            <span className={styles.headingIcon}><FaGraduationCap /></span>
                            Chapterwise Notes
                        </h2>
                    </div>

                    {/* ── Filters + Search (single row) ── */}
                    <div className={styles.filterControlsRow}>
                        <div className={styles.filterSelectWrapper}>
                            <span className={styles.filterLabel}>Exam / Class</span>
                            <div className={styles.filterSelectInner}>
                                <select
                                    className={styles.filterSelect}
                                    value={notesClassFilter}
                                    onChange={(e) => { setNotesClassFilter(e.target.value); setNotesSearch(''); }}
                                >
                                    <option value="jeeneet">JEE / NEET</option>
                                    <option value="class10">Class 10</option>
                                    <option value="class9">Class 9</option>
                                    <option value="class8">Class 8</option>
                                    <option value="class7">Class 7</option>
                                    <option value="class6">Class 6</option>
                                </select>
                                <span className={styles.filterSelectArrow}>▾</span>
                            </div>
                        </div>
                        <div className={styles.filterSelectWrapper}>
                            <span className={styles.filterLabel}>Subject</span>
                            <div className={styles.filterSelectInner}>
                                {isSchoolMode ? (
                                    <select
                                        className={styles.filterSelect}
                                        value={notesSchoolSubject}
                                        onChange={(e) => setNotesSchoolSubject(e.target.value)}
                                    >
                                        <option value="science">Science</option>
                                        <option value="mathematics">Mathematics</option>
                                    </select>
                                ) : (
                                    <select
                                        className={styles.filterSelect}
                                        value={notesFilter}
                                        onChange={(e) => setNotesFilter(e.target.value)}
                                    >
                                        {notesFilters.map((f) => (
                                            <option key={f.id} value={f.id}>{f.label}</option>
                                        ))}
                                    </select>
                                )}
                                <span className={styles.filterSelectArrow}>▾</span>
                            </div>
                        </div>
                        <div className={styles.filterSearchBox}>
                            <FaSearch className={styles.filterSearchIcon} />
                            <input
                                type="text"
                                value={notesSearch}
                                onChange={(e) => setNotesSearch(e.target.value)}
                                placeholder="Search chapter..."
                                className={styles.filterSearchInput}
                                aria-label="Search chapter"
                            />
                        </div>
                    </div>

                    <div className={styles.csGrid}>
                        {filteredChapterCards.map((ch) => {
                            const title = getBilingualTitle(ch.title);
                            const isSchool = !!ch._school;
                            const to = isSchool
                                ? `/school-notes/${ch.programKey}/${ch.slug}`
                                : `/notes/${ch.subject}/${ch.slug}`;
                            const returnState = isSchool
                                ? { returnSection: 'chapterwise', returnTab: 'chapterwise', returnHash: '#chapterwise-notes', returnProgramKey: ch.programKey }
                                : { returnSection: 'chapterwise', returnTab: 'chapterwise', returnHash: '#chapterwise-notes' };
                            const subjectColor = isSchool
                                ? (ch.subject === 'science' ? 'physics' : 'mathematics')
                                : ch.subject;
                            return (
                                <Link
                                    key={isSchool ? `${ch.programKey}/${ch.slug}` : `${ch.subject}/${ch.slug}`}
                                    to={to}
                                    state={returnState}
                                    className={`${styles.csCard} ${styles[`csCard_${subjectColor}`]}`}
                                >
                                    <div className={styles.csCardThumb}>
                                        <img
                                            src={isSchool
                                                ? `/school-notes/thumbs/${ch.programKey}.svg`
                                                : `/notes/thumbs/${ch.subject}/${ch.slug}.svg?v=1`}
                                            alt={title.english}
                                            loading="lazy"
                                            onError={(e) => {
                                                e.currentTarget.src = SUBJECT_THUMBS[ch.subject] || SUBJECT_THUMBS.mathematics;
                                            }}
                                        />
                                        {isSchool && <div className={styles.csLangBadge}>EN / HI</div>}
                                        {!isSchool && <div className={styles.csLangBadge}>Language: English/Hindi</div>}
                                    </div>
                                    <div className={styles.csCardNum}>Chapter {ch.chapterNo}</div>
                                    <div className={styles.csCardTitleStack}>
                                        <div className={styles.csCardTitleEnglish}>{title.english}</div>
                                        <div className={styles.csCardTitleHindi}>{title.hindi}</div>
                                    </div>
                                    <div className={styles.csCardDesc}>
                                        {isSchool
                                            ? `${schoolProgram?.classLabel || ''} · ${ch.subject === 'science' ? 'Science' : 'Mathematics'}`
                                            : `${ch.subjectMeta?.label || ch.subject} · ${ch.exam || 'JEE / NEET'}`
                                        }
                                    </div>
                                    <div className={styles.csCardTags}>
                                        <span className={styles.csOpenHint}>Open</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                    {filteredChapterCards.length === 0 && (
                        <div className={styles.csEmptyState}>
                            <p>No chapter matched your search.</p>
                            <small>Try a different keyword or clear the search.</small>
                        </div>
                    )}
                </section>
            </div>
            )}

            <div className={styles.container}>
                {libraryConfig.filter(c => activeTab === c.id).map((category) => {
                    const activeSubId = activeSub[category.id] || category.subcategories[0]?.id;
                    const cacheKey = `${category.id}/${activeSubId}`;
                    const isLoading = loadingKeys.has(cacheKey);
                    const files = materialsCache[cacheKey] || [];
                    const searchText = (librarySearch[category.id] || '').trim().toLowerCase();
                    const visibleFiles =
                        searchText.length === 0
                            ? files
                            : files.filter((file) => file.title.toLowerCase().includes(searchText));

                    return (
                        <section key={category.id} className={styles.categorySection}>
                            <div className={styles.categoryHeadingRow}>
                                <h2 className={styles.categoryHeading}>
                                    <span className={styles.headingIcon}>{category.icon}</span>
                                    {category.mainCategory}
                                </h2>
                            </div>
                            <>
                                <div className={styles.filterControlsRow}>
                                    <div className={styles.filterSelectWrapper}>
                                        <span className={styles.filterLabel}>Class/Section</span>
                                        <div className={styles.filterSelectInner}>
                                            <select
                                                className={styles.filterSelect}
                                                value={activeSubId}
                                                onChange={(e) => {
                                                    const subId = e.target.value;
                                                    setActiveSub(prev => ({ ...prev, [category.id]: subId }));
                                                    fetchMaterials(category.id, subId);
                                                }}
                                            >
                                                {category.subcategories.map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.label}</option>
                                                ))}
                                            </select>
                                            <span className={styles.filterSelectArrow}>▾</span>
                                        </div>
                                    </div>
                                    <div className={styles.filterSearchBox}>
                                        <FaSearch className={styles.filterSearchIcon} />
                                        <input
                                            type="text"
                                            value={librarySearch[category.id] || ''}
                                            onChange={(e) => setLibrarySearch(prev => ({ ...prev, [category.id]: e.target.value }))}
                                            placeholder="Search document..."
                                            className={styles.filterSearchInput}
                                            aria-label={`Search ${category.mainCategory}`}
                                        />
                                    </div>
                                </div>

                            {/* ── Inline file grid ── */}
                            {isLoading ? (
                                <div className={styles.loader}>Loading materials…</div>
                            ) : visibleFiles.length > 0 ? (
                                <div className={styles.fileGrid}>
                                    {visibleFiles.map((file) => {
                                        const progress = downloadProgress[file.id];
                                        const isDownloading = progress !== undefined;
                                        return (
                                            <div key={file.id} className={styles.fileCard}>
                                                <div className={styles.fileThumbnail}>
                                                    {file.thumbUrl ? (
                                                        <img src={file.thumbUrl} alt={file.title} className={styles.previewImg} />
                                                    ) : (
                                                        <div className={styles.pdfFallback}><FaFilePdf size={50} /></div>
                                                    )}
                                                    {isDownloading && (
                                                        <div className={styles.progressOverlay}>
                                                            <div className={styles.progressCircle}><span>{progress}%</span></div>
                                                            <div className={styles.progressBarBg}>
                                                                <div className={styles.progressBarFill} style={{ width: `${progress}%` }}></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={styles.fileInfo}>
                                                    <h4 className={styles.fileName}>{file.title}</h4>
                                                    <div className={styles.fileActions}>
                                                        <a href={file.pdfUrl} target="_blank" rel="noreferrer" className={styles.viewBtn}><FaEye /> View</a>
                                                        <button
                                                            className={styles.downloadActionBtn}
                                                            onClick={() => handleDownload(file.pdfUrl, file.title, file.id)}
                                                            disabled={isDownloading}
                                                        >
                                                            <FaDownload /> {isDownloading ? `${progress}%` : 'Download'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    <FaFilePdf size={36} />
                                    <p>{searchText ? 'No documents matched your search.' : 'No documents found in this section yet. Check back soon!'}</p>
                                </div>
                            )}
                            </>
                        </section>
                    );
                })}
            </div>

        {/* ══════════════════════════════════════════════════
            JEE/NEET MASTER HANDBOOKS
            ══════════════════════════════════════════════════ */}
        {(activeTab === 'all' || activeTab === 'handbooks') && (
        <div className={`${styles.container} ${styles.handbooksContainer}`}>
        <section className={`${styles.categorySection} ${styles.handbooksSection}`} ref={hbRef} id="master-handbooks">
            <div className={styles.categoryHeadingRow}>
                <h2 className={styles.categoryHeading}>
                    <span className={styles.headingIcon}><FaBookOpen /></span>
                    Master Handbooks
                </h2>
            </div>
            <div className={styles.filterControlsRow}>
                <div className={styles.filterSelectWrapper}>
                    <span className={styles.filterLabel}>Class/Exam</span>
                    <div className={styles.filterSelectInner}>
                        <select
                            className={styles.filterSelect}
                            value={hbPrimary}
                            onChange={(e) => setHbPrimary(e.target.value)}
                        >
                            {CS_PRIMARY_FILTERS.map((f) => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                            ))}
                        </select>
                        <span className={styles.filterSelectArrow}>▾</span>
                    </div>
                </div>
                <div className={styles.filterSearchBox}>
                    <FaSearch className={styles.filterSearchIcon} />
                    <input
                        type="text"
                        value={hbSearch}
                        onChange={(e) => setHbSearch(e.target.value)}
                        placeholder="Search handbook..."
                        className={styles.filterSearchInput}
                        aria-label="Search handbooks"
                    />
                </div>
            </div>

            {/* Handbooks grid */}
            <div className={styles.csGrid}>
                {(() => {
                    if (filteredHandbooks.length === 0) return (
                        <div className={styles.csEmptyState}>
                            <p>Handbooks for this selection are coming soon!</p>
                            <small>We're expanding Master Handbooks across all classes and subjects.</small>
                        </div>
                    );
                    return filteredHandbooks.map(handbook => (
                        handbook.comingSoon ? (
                            <div
                                key={handbook.file}
                                className={`${styles.csCard} ${styles[`csCard_${handbook.subject}`]} ${styles.csCardComingSoon}`}
                            >
                                <div className={styles.csCardThumb}>
                                    <img
                                        src={`/cheatsheets/thumbs/sheets/${handbook.file.replace('.html', '')}.svg?v=3`}
                                        alt={`${handbook.title} cover`}
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.src = handbook.thumb || SUBJECT_THUMBS[handbook.subject] || ''; }}
                                    />
                                    <div className={styles.csLangBadge}>Language: English/Hindi</div>
                                </div>
                                <div className={styles.csCardNum}>{handbook.num}</div>
                                <div className={styles.csCardTitle}>{handbook.title}</div>
                                <div className={styles.csCardDesc}>{handbook.desc}</div>
                                <div className={styles.csCardTags}>
                                    {handbook.tags.map((t, i) => (
                                        <span key={i} className={styles.csTag}>{t}</span>
                                    ))}
                                    <span className={styles.csComingSoonBadge}>Coming Soon</span>
                                </div>
                                <div className={styles.csChapterCount}>
                                    Chapters: {handbook.chapters} {handbook.chapterLabel || 'Comprehensive Chapters'}
                                </div>
                            </div>
                        ) : (
                            <Link
                                key={handbook.file}
                                to={`/cheatsheet/${handbook.file.replace('.html', '')}`}
                                state={{ returnSection: 'handbooks', returnTab: 'handbooks', returnHash: '#master-handbooks' }}
                                className={`${styles.csCard} ${styles[`csCard_${handbook.subject}`]}`}
                            >
                                <div className={styles.csCardThumb}>
                                    <img
                                        src={`/cheatsheets/thumbs/sheets/${handbook.file.replace('.html', '')}.svg?v=3`}
                                        alt={`${handbook.title} cover`}
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.src = handbook.thumb || SUBJECT_THUMBS[handbook.subject] || ''; }}
                                    />
                                    <div className={styles.csLangBadge}>Language: English/Hindi</div>
                                </div>
                                <div className={styles.csCardNum}>{handbook.num}</div>
                                <div className={styles.csCardTitle}>{handbook.title}</div>
                                <div className={styles.csCardDesc}>{handbook.desc}</div>
                                <div className={styles.csCardTags}>
                                    {handbook.tags.map((t, i) => (
                                        <span key={i} className={`${styles.csTag} ${handbook.hot && i === 0 ? styles.csTagHot : ''}`}>{t}</span>
                                    ))}
                                    <span className={styles.csOpenHint}>Open</span>
                                </div>
                                <div className={styles.csChapterCount}>
                                    Chapters: {handbook.chapters} {handbook.chapterLabel || 'Comprehensive Chapters'}
                                </div>
                            </Link>
                        )
                    ));
                })()}
            </div>
        </section>
        </div>
        )}

        {/* ══════════════════════════════════════════════════
            HIGH SCORING CHEATSHEETS (All View Placement)
            ══════════════════════════════════════════════════ */}
        {(activeTab === 'all') && (
        <div className={styles.container}>
        <section className={styles.categorySection} ref={csRef} id="cheatsheets">
            <div className={styles.categoryHeadingRow}>
                <h2 className={styles.categoryHeading}>
                    <span className={styles.headingIcon}><FaClipboardList /></span>
                    JEE/NEET Cheatsheets
                </h2>
            </div>
            <div className={styles.filterControlsRow}>
                <div className={styles.filterSelectWrapper}>
                    <span className={styles.filterLabel}>Subject</span>
                    <div className={styles.filterSelectInner}>
                        <select
                            className={styles.filterSelect}
                            value={csFilter}
                            onChange={(e) => setCsFilter(e.target.value)}
                        >
                            {revisionSubjectFilters.map((f) => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                            ))}
                        </select>
                        <span className={styles.filterSelectArrow}>▾</span>
                    </div>
                </div>
                <div className={styles.filterSearchBox}>
                    <FaSearch className={styles.filterSearchIcon} />
                    <input
                        type="text"
                        value={csSearch}
                        onChange={(e) => setCsSearch(e.target.value)}
                        placeholder="Search cheatsheet..."
                        className={styles.filterSearchInput}
                        aria-label="Search cheatsheets"
                    />
                </div>
            </div>

            <div className={styles.csGrid}>
                {(() => {
                    if (filteredSheets.length === 0) return (
                        <div className={styles.csEmptyState}>
                            <p>Sheets for this subject are coming soon!</p>
                            <small>We're building high-yield JEE/NEET revision sheets across all subjects. Stay tuned!</small>
                        </div>
                    );
                    return filteredSheets.map(sheet => (
                    <Link
                        key={sheet.file}
                        to={`/cheatsheet/${sheet.file.replace('.html', '')}`}
                        state={{ returnSection: 'cheatsheets', returnTab: 'sheets', returnHash: '#cheatsheets' }}
                        className={`${styles.csCard} ${styles[`csCard_${sheet.subject}`]}`}
                    >
                        <div className={styles.csCardThumb}>
                            <img
                                src={`/cheatsheets/thumbs/sheets/${sheet.file.replace('.html', '')}.svg?v=3`}
                                alt={`${sheet.title} cover`}
                                loading="lazy"
                                onError={(e) => { e.currentTarget.src = SUBJECT_THUMBS[sheet.subject] || ''; }}
                            />
                            <div className={styles.csLangBadge}>Language: English/Hindi</div>
                        </div>
                        <div className={styles.csCardNum}>{sheet.num}</div>
                        <div className={styles.csCardTitle}>{sheet.title}</div>
                        <div className={styles.csCardDesc}>{sheet.desc}</div>
                        <div className={styles.csCardTags}>
                            {sheet.tags.map((t, i) => (
                                <span key={i} className={`${styles.csTag} ${sheet.hot && i === 0 ? styles.csTagHot : ''}`}>{t}</span>
                            ))}
                            <span className={styles.csOpenHint}>Open</span>
                        </div>
                    </Link>
                    ));
                })()}
            </div>
        </section>
        </div>
        )}

        {(activeTab === 'all') && (
        <div className={styles.container}>
            {libraryConfig.map((category) => {
                const activeSubId = activeSub[category.id] || category.subcategories[0]?.id;
                const cacheKey = `${category.id}/${activeSubId}`;
                const isLoading = loadingKeys.has(cacheKey);
                const files = materialsCache[cacheKey] || [];
                const searchText = (librarySearch[category.id] || '').trim().toLowerCase();
                const visibleFiles =
                    searchText.length === 0
                        ? files
                        : files.filter((file) => file.title.toLowerCase().includes(searchText));

                return (
                    <section key={category.id} className={styles.categorySection}>
                        <div className={styles.categoryHeadingRow}>
                            <h2 className={styles.categoryHeading}>
                                <span className={styles.headingIcon}>{category.icon}</span>
                                {category.mainCategory}
                            </h2>
                        </div>
                        <>
                            <div className={styles.filterControlsRow}>
                                <div className={styles.filterSelectWrapper}>
                                    <span className={styles.filterLabel}>Class/Section</span>
                                    <div className={styles.filterSelectInner}>
                                        <select
                                            className={styles.filterSelect}
                                            value={activeSubId}
                                            onChange={(e) => {
                                                const subId = e.target.value;
                                                setActiveSub(prev => ({ ...prev, [category.id]: subId }));
                                                fetchMaterials(category.id, subId);
                                            }}
                                        >
                                            {category.subcategories.map((sub) => (
                                                <option key={sub.id} value={sub.id}>{sub.label}</option>
                                            ))}
                                        </select>
                                        <span className={styles.filterSelectArrow}>▾</span>
                                    </div>
                                </div>
                                <div className={styles.filterSearchBox}>
                                    <FaSearch className={styles.filterSearchIcon} />
                                    <input
                                        type="text"
                                        value={librarySearch[category.id] || ''}
                                        onChange={(e) => setLibrarySearch(prev => ({ ...prev, [category.id]: e.target.value }))}
                                        placeholder="Search document..."
                                        className={styles.filterSearchInput}
                                        aria-label={`Search ${category.mainCategory}`}
                                    />
                                </div>
                            </div>

                        {isLoading ? (
                            <div className={styles.loader}>Loading materials…</div>
                        ) : visibleFiles.length > 0 ? (
                            <div className={styles.fileGrid}>
                                {visibleFiles.map((file) => {
                                    const progress = downloadProgress[file.id];
                                    const isDownloading = progress !== undefined;
                                    return (
                                        <div key={file.id} className={styles.fileCard}>
                                            <div className={styles.fileThumbnail}>
                                                {file.thumbUrl ? (
                                                    <img src={file.thumbUrl} alt={file.title} className={styles.previewImg} />
                                                ) : (
                                                    <div className={styles.pdfFallback}><FaFilePdf size={50} /></div>
                                                )}
                                                {isDownloading && (
                                                    <div className={styles.progressOverlay}>
                                                        <div className={styles.progressCircle}><span>{progress}%</span></div>
                                                        <div className={styles.progressBarBg}>
                                                            <div className={styles.progressBarFill} style={{ width: `${progress}%` }}></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.fileInfo}>
                                                <h4 className={styles.fileName}>{file.title}</h4>
                                                <div className={styles.fileActions}>
                                                    <a href={file.pdfUrl} target="_blank" rel="noreferrer" className={styles.viewBtn}><FaEye /> View</a>
                                                    <button
                                                        className={styles.downloadActionBtn}
                                                        onClick={() => handleDownload(file.pdfUrl, file.title, file.id)}
                                                        disabled={isDownloading}
                                                    >
                                                        <FaDownload /> {isDownloading ? `${progress}%` : 'Download'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.emptyState}>
                                <FaFilePdf size={36} />
                                <p>{searchText ? 'No documents matched your search.' : 'No documents found in this section yet. Check back soon!'}</p>
                            </div>
                        )}
                        </>
                    </section>
                );
            })}
        </div>
        )}

        {/* ══════════════════════════════════════════════════
            HIGH SCORING CHEATSHEETS (Cheatsheet Hub)
            ══════════════════════════════════════════════════ */}
        {(activeTab === 'sheets') && (
        <div className={styles.container}>
        <section className={styles.categorySection} ref={csRef} id="cheatsheets">
            <div className={styles.categoryHeadingRow}>
                <h2 className={styles.categoryHeading}>
                    <span className={styles.headingIcon}><FaClipboardList /></span>
                    JEE/NEET Cheatsheets
                </h2>
            </div>
            {/* Subject + Search */}
            <div className={styles.filterControlsRow}>
                <div className={styles.filterSelectWrapper}>
                    <span className={styles.filterLabel}>Subject</span>
                    <div className={styles.filterSelectInner}>
                        <select
                            className={styles.filterSelect}
                            value={csFilter}
                            onChange={(e) => setCsFilter(e.target.value)}
                        >
                            {revisionSubjectFilters.map((f) => (
                                <option key={f.id} value={f.id}>{f.label}</option>
                            ))}
                        </select>
                        <span className={styles.filterSelectArrow}>▾</span>
                    </div>
                </div>
                <div className={styles.filterSearchBox}>
                    <FaSearch className={styles.filterSearchIcon} />
                    <input
                        type="text"
                        value={csSearch}
                        onChange={(e) => setCsSearch(e.target.value)}
                        placeholder="Search cheatsheet..."
                        className={styles.filterSearchInput}
                        aria-label="Search cheatsheets"
                    />
                </div>
            </div>

            {/* Cards grid */}
            <div className={styles.csGrid}>
                {(() => {
                    if (filteredSheets.length === 0) return (
                        <div className={styles.csEmptyState}>
                            <p>Sheets for this subject are coming soon!</p>
                            <small>We're building high-yield JEE/NEET revision sheets across all subjects. Stay tuned!</small>
                        </div>
                    );
                    return filteredSheets.map(sheet => (
                    <Link
                        key={sheet.file}
                        to={`/cheatsheet/${sheet.file.replace('.html', '')}`}
                        state={{ returnSection: 'cheatsheets', returnTab: 'sheets', returnHash: '#cheatsheets' }}
                        className={`${styles.csCard} ${styles[`csCard_${sheet.subject}`]}`}
                    >
                        <div className={styles.csCardThumb}>
                            <img
                                src={`/cheatsheets/thumbs/sheets/${sheet.file.replace('.html', '')}.svg?v=3`}
                                alt={`${sheet.title} cover`}
                                loading="lazy"
                                onError={(e) => { e.currentTarget.src = SUBJECT_THUMBS[sheet.subject] || ''; }}
                            />
                        </div>
                        <div className={styles.csCardNum}>{sheet.num}</div>
                        <div className={styles.csCardTitle}>{sheet.title}</div>
                        <div className={styles.csCardDesc}>{sheet.desc}</div>
                        <div className={styles.csCardTags}>
                            {sheet.tags.map((t, i) => (
                                <span key={i} className={`${styles.csTag} ${sheet.hot && i === 0 ? styles.csTagHot : ''}`}>{t}</span>
                            ))}
                            <span className={styles.csOpenHint}>Open</span>
                        </div>
                    </Link>
                    ));
                })()}
            </div>
        </section>
        </div>
        )}

        {/* ── CTA BANNER ── */}
        <section className={styles.ctaBanner}>
            <div className={styles.ctaBannerInner}>
                <h2 className={styles.ctaBannerTitle}>Ready to Start Practice?</h2>
                <p className={styles.ctaBannerSub}>PrrepX Test - One Subscription, Complete Access Model: 471+ Premium Scheduled Tests, Unlimited Open Zone Tests &amp; 80+ Free PYQ Tests.</p>
                <div className={styles.ctaBannerBtns}>
                    <a href="https://testseries.prrepxpathshala.live/test-series/pyq" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtn}>Free PYQ Tests</a>
                    <a href="https://testseries.prrepxpathshala.live/test-series/jee-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}>JEE Premium Tests</a>
                    <a href="https://testseries.prrepxpathshala.live/test-series/neet-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}>NEET Premium Tests</a>
                </div>
            </div>
        </section>

        {/* ── FOOTER NOTE ── */}
        <div className={styles.footerNote}>
            <p>© {new Date().getFullYear()}-{String(new Date().getFullYear() + 1).slice(-2)} PrrepX Pathshala · Led by 100% IIT/NIT & top medical Alumni.</p>
        </div>

    </div>
    );
};

export default StudyMaterial;